import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function AdminAddPrepaid() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    full_name: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    box_slots_purchased: "30",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("add-prepaid-user", {
        body: {
          ...formData,
          box_slots_purchased: parseInt(formData.box_slots_purchased),
          send_welcome_email: sendWelcomeEmail,
        },
      });

      if (error) throw error;

      toast.success(
        `Successfully added ${formData.full_name} with ${formData.box_slots_purchased} prepaid boxes${
          sendWelcomeEmail ? " and sent welcome email" : ""
        }`
      );
      
      // Reset form
      setFormData({
        email: "",
        full_name: "",
        phone: "",
        address_line1: "",
        address_line2: "",
        city: "",
        state: "",
        zip: "",
        box_slots_purchased: "30",
      });
      setSendWelcomeEmail(true);
    } catch (error) {
      console.error("Error adding prepaid user:", error);
      toast.error(error instanceof Error ? error.message : "Failed to add prepaid user");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Sanitize phone number - strip non-digits
    if (name === "phone") {
      const sanitized = value.replace(/\D/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: sanitized,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/admin")}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Admin
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>Add Prepaid User</CardTitle>
            <CardDescription>
              Add a new user with prepaid box storage slots
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="4259856175"
                    maxLength={10}
                    required
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Enter 10-digit phone number (digits only)
                  </p>
                </div>

                <div className="col-span-2">
                  <Label htmlFor="address_line1">Address Line 1</Label>
                  <Input
                    id="address_line1"
                    name="address_line1"
                    value={formData.address_line1}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="address_line2">Address Line 2 (Optional)</Label>
                  <Input
                    id="address_line2"
                    name="address_line2"
                    value={formData.address_line2}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                    maxLength={2}
                    placeholder="WA"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="zip">ZIP Code</Label>
                  <Input
                    id="zip"
                    name="zip"
                    value={formData.zip}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="box_slots_purchased">Box Slots</Label>
                  <Input
                    id="box_slots_purchased"
                    name="box_slots_purchased"
                    type="number"
                    min="1"
                    value={formData.box_slots_purchased}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-span-2 flex items-center space-x-2">
                  <Checkbox
                    id="send_welcome_email"
                    checked={sendWelcomeEmail}
                    onCheckedChange={(checked) => setSendWelcomeEmail(checked === true)}
                  />
                  <Label
                    htmlFor="send_welcome_email"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Send welcome email with magic link
                  </Label>
                </div>
              </div>

              <Button type="submit" disabled={isLoading} className="w-full">
                {isLoading ? "Adding User..." : "Add Prepaid User"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
